import { type ReactNode } from 'react';
import { useChartTheme } from './theme.js';

export interface ChartTooltipPayload {
  name?: string;
  value?: number | string;
  color?: string;
  dataKey?: string;
  payload?: Record<string, unknown>;
}

export interface ChartTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: ChartTooltipPayload[];
  formatLabel?: (label: string | number) => ReactNode;
  /**
   * Value formatter: matches `BaseChartProps.valueFormatter`. Receives
   * the numeric series value; non-numeric rows pass through as-is.
   */
  formatValue?: (value: number) => ReactNode;
}

/**
 * Themed Recharts tooltip: pass as `content={<ChartTooltip />}` to any
 * `<Tooltip>` element. Compact rendering matching our `<Paper>` look.
 */
export function ChartTooltip({ active, label, payload, formatLabel, formatValue }: ChartTooltipProps): ReactNode {
  const theme = useChartTheme();
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      style={{
        background: theme.tooltipBg,
        border: `1px solid ${theme.tooltipBorder}`,
        borderRadius: 'var(--rr-radius-md)',
        boxShadow: 'var(--rr-shadow-md)',
        padding: 'var(--rr-spacing-sm) var(--rr-spacing-md)',
        fontFamily: 'var(--rr-font-system)',
        fontSize: 'var(--rr-fs-bodySmall)',
        color: theme.tooltipText,
        minWidth: 140
      }}
    >
      {label !== undefined ? (
        <div style={{ fontWeight: 'var(--rr-fw-semibold)', marginBottom: 6 }}>
          {formatLabel ? formatLabel(label) : label}
        </div>
      ) : null}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 10, height: 10, borderRadius: 2,
            background: p.color, flexShrink: 0
          }} />
          <span style={{ flex: 1, color: theme.axisLabel }}>{p.name ?? p.dataKey}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 'var(--rr-fw-medium)' }}>
            {formatValue && typeof p.value === 'number' ? formatValue(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}
